import {useLazyQuery, useMutation} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {Dropdown, Form, Input, Pagination, Select, Table} from 'antd';
import {GET_ASSET_CLASS_INSTRUMENTS} from 'graphql/queries/assetManagement';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import styles from '../assetClassOverview.module.scss';
import shell from 'pages/AssetManagement/v2/pageShell.module.scss';
import v2 from '../../v2/instrumentV2.module.scss';
import {formatEnumType, sanitizeInput} from 'utils/helper';
import {
  instrumentKeysFor,
  supportsInventoryPrediction,
  defaultInstrumentKey,
} from '../../modules/instrumentKeys';
import dotsIcon from 'images/dots.svg';
import {SearchOutlined} from '@ant-design/icons';
import {useEffect, useState} from 'react';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {DELETE_GROUPED_FINANCIAL_INSTRUMENT} from 'graphql/mutations/assetManagement';
import {toast} from 'react-toastify';
import filterIcon from 'icons/filter.svg';
import {assetClassStatus} from 'pages/AssetManagement/modules/constants';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {handleUpdateGroupedInstruments} from 'redux/store/assetClass';
import {useDispatch, useSelector} from 'react-redux';
import {NavTabsEnum, supportedLanguages} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {assetClassLabel} from 'pages/AssetManagement/modules/instrumentKeys';
import {
  inventoryColumns,
  inventoryScrollWidth,
  stickyLeft,
} from 'pages/AssetManagement/modules/inventoryColumns';
import {useInstrumentRowActions} from './useInstrumentRowActions';

// Spelled out, never the two-letter code — an operator should not have to
// decode "EN".
const languageName = code =>
  supportedLanguages.find(language => language.value === code)?.label ?? code;

export const AssetClassInstruments = () => {
  const route = useLocation();
  const searchSubRoute = new URLSearchParams(route.search).get('subRoute');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteGroup, setDeleteGroup] = useState({
    open: false,
    id: null,
    input: {},
  });
  const [limit, setLimit] = useState(10);
  // What the list is actually filtered by, as opposed to what the panel's form
  // currently holds. The two diverge the moment someone edits without applying.
  const [appliedFilters, setAppliedFilters] = useState({
    status: undefined,
    listingType: undefined,
    search: undefined,
  });
  const [activeSubTab, setActiveSubTab] = useState(searchSubRoute || 'SINGLE');
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [form] = Form.useForm();
  const {key} = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // EQUITIES holds both PUBLIC_EQUITY and IPO, so the type is chosen rather
  // than inferred from the asset class.
  const instrumentKeyOptions = instrumentKeysFor(key);
  const [instrumentKey, setInstrumentKey] = useState(defaultInstrumentKey(key));

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const [fetchData, {data, loading, refetch}] = useLazyQuery(
    GET_ASSET_CLASS_INSTRUMENTS,
  );

  useEffect(() => {
    if (activeSubTab) {
      fetchData({
        variables: {
          input: {
            explore: false,
            instrumentKey,
            assetClassKey: key,
            // Carried across a type or sub-tab change, because the Filter badge
            // and the panel both still show them. Dropping them here served an
            // unfiltered list under a badge claiming otherwise — now visible
            // with search, where the box would still hold the term.
            status: appliedFilters.status,
            listingType: appliedFilters.listingType,
            search: appliedFilters.search,
            page: 1,
            paginate: true,
            groups: activeSubTab !== 'SINGLE',
            limit: 10,
          },
        },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: true,
        onError: error => toast.error(error.message),
      });
    }
    setCurrentPage(1);
  }, [activeSubTab, instrumentKey]);

  const [ungroupFinancialInstruments, {loading: loadingDeleteGrouped}] =
    useMutation(DELETE_GROUPED_FINANCIAL_INSTRUMENT);

  // Shared with the Multi Asset → Instruments workspace so both lists offer the
  // same row menu.
  const {actionsColumn, actionModals} = useInstrumentRowActions({
    instrumentKey,
    pageOperations,
    refetch,
    navigate,
  });

  // V2 column set. The old table carried 13 columns — six of them a nested
  // inventory group — which pushed the fields operators actually scan off the
  // right edge. Inventory collapses to a health bar; its numbers live in the
  // tooltip, and the full detail is still one click away in the row menu.
  const statusChip = {
    ENABLED: v2.chipOk,
    DISABLED: v2.chipMute,
    PENDING: v2.chipWarn,
  };

  const riskChip = {
    CONSERVATIVE: v2.chipBrand,
    MODERATE: v2.chipWarn,
    AGGRESSIVE: v2.chipStop,
  };

  const columns = [
    {
      title: 'Ticker',
      dataIndex: 'ticker',
      key: 'ticker',
      ...stickyLeft(instrumentKey, 130),
      render: (text, record) => (
        <span className={v2.cellTicker}>{record?.ticker || '—'}</span>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...stickyLeft(instrumentKey, 240),
      render: (text, record) => (
        <div>
          <div className={v2.cellName}>{record?.name}</div>
          <div className={v2.cellSub}>
            {[
              record?.listingType === 'UNLISTED' ? 'Unlisted' : 'Listed',
              record?.marketPhase && formatEnumType(record.marketPhase),
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (text, record) => (
        <span
          className={`${v2.chip} ${statusChip[record?.status] || v2.chipMute}`}>
          {record?.status ? formatEnumType(record.status) : '—'}
        </span>
      ),
    },
    {
      title: 'Risk',
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 130,
      render: (text, record) =>
        record?.riskLevel ? (
          <span
            className={`${v2.chip} ${
              riskChip[record.riskLevel] || v2.chipMute
            }`}>
            {formatEnumType(record.riskLevel)}
          </span>
        ) : (
          <span className={v2.healthEmpty}>—</span>
        ),
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      width: 110,
      render: (text, record) => (
        <span className={v2.cellTicker}>
          {record?.currency?.currencyCode || '—'}
        </span>
      ),
    },
    // Six columns rather than the health bar that stood here before. The bar
    // put every number behind a hover, so an operator could not compare
    // available units or thresholds down the column — which is the whole reason
    // this is a table. `inventoryColumns` decides which types get them.
    ...inventoryColumns(instrumentKey),
    actionsColumn,
  ];

  const groupColumns = [
    {
      title: 'Group',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <div className={v2.groupCell}>
          {record?.banner ? (
            <img className={v2.groupBanner} src={record.banner} alt="" />
          ) : (
            <div className={`${v2.groupBanner} ${v2.groupBannerEmpty}`} />
          )}
          <div>
            <div className={v2.cellName}>{record?.title}</div>
            <div className={v2.cellSub}>
              {record?.description || 'No description'}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Instruments',
      dataIndex: 'count',
      key: 'count',
      render: (text, record) => (
        <span className={v2.cellTicker}>
          {record?.instruments?.length ?? 0}
        </span>
      ),
    },
    {
      title: 'Risk',
      dataIndex: 'risk',
      key: 'risk',
      render: (text, record) =>
        record?.riskLevel ? (
          <span
            className={`${v2.chip} ${
              {
                AGGRESSIVE: v2.chipStop,
                MODERATE: v2.chipWarn,
                CONSERVATIVE: v2.chipOk,
              }[record.riskLevel] ?? v2.chipMute
            }`}>
            {formatEnumType(record.riskLevel)}
          </span>
        ) : (
          <span className={v2.healthEmpty}>—</span>
        ),
    },
    {
      title: 'Languages',
      dataIndex: 'lang',
      key: 'language',
      render: (text, record) => {
        // `multilang` carries every version including the base one, so it is
        // the honest count of what an investor could be shown.
        const total = record?.multilang?.length || 1;
        return (
          <div>
            <div className={v2.cellName}>{languageName(record?.lang)}</div>
            {total > 1 && (
              <div className={v2.cellSub}>
                +{total - 1} translation{total - 1 === 1 ? '' : 's'}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (text, record) => (
        <Dropdown
          trigger="click"
          menu={{
            items: [
              {
                key: 1,
                label: 'Update',
                disabled: !pageOperations?.update,
                onClick: e => {
                  e.domEvent.stopPropagation();
                  dispatch(handleUpdateGroupedInstruments({...record}));
                  navigate(
                    `/dashboard/asset-management/${key}/${instrumentKey}/update-instrument-group/${record?.groupId}`,
                  );
                },
              },
              {
                key: 2,
                label: 'Delete',
                disabled: !pageOperations?.delete,
                onClick: e => {
                  e.domEvent.stopPropagation();
                  setDeleteGroup({
                    open: true,
                    id: record?.groupId,
                    input: {...record},
                  });
                },
              },
            ],
          }}>
          <img
            src={dotsIcon}
            role="button"
            onClick={e => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  /**
   * One fetch for the list, given a filter, a page and a page size.
   *
   * This replaces a `handleFilter(values, limit, page)` that carried three
   * separate defects, all of them from serving as both the form's `onFinish`
   * and the pager's handler:
   *
   *  - `page: status || listingType ? 1 : page` forced page 1 whenever a filter
   *    was set. Filtering to the 15 enabled bonds and clicking page 2 lit up
   *    page 2 and re-served page 1, so the last 5 were unreachable — and the
   *    same held for every filtered list in the app.
   *  - The `limit` parameter shadowed the `limit` state. antd calls `onFinish`
   *    with only `(values)`, so applying a filter ran `setLimit(undefined)` and
   *    queried `limit: undefined`; it looked fine only because the server
   *    happens to default to 10.
   *  - Resetting to page 1 belongs to *changing* the filter, not to every
   *    fetch. That is now the caller's decision.
   */
  const runQuery = ({
    filters = appliedFilters,
    page = 1,
    size = limit,
  } = {}) => {
    refetch({
      input: {
        explore: false,
        instrumentKey,
        assetClassKey: key,
        status: filters.status,
        listingType: filters.listingType,
        search: filters.search,
        groups: activeSubTab !== 'SINGLE',
        paginate: true,
        page,
        limit: size,
      },
    });
  };

  // What is typed in the toolbar, as opposed to what the list is filtered by.
  // Two states so the box stays responsive while the query waits for a pause
  // in the typing.
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const trimmed = searchInput.trim();
    const term = trimmed ? sanitizeInput(trimmed) : undefined;
    // Covers the first render, and any edit that lands back on the applied
    // term — neither should refetch.
    if (term === appliedFilters.search) return undefined;

    const timer = setTimeout(() => {
      const next = {...appliedFilters, search: term};
      setAppliedFilters(next);
      setCurrentPage(1);
      runQuery({filters: next, page: 1});
    }, 500);
    return () => clearTimeout(timer);
    // Only the typed term: re-running this when appliedFilters changes would
    // fire a second query straight after the panel applies a status.
  }, [searchInput]);

  // Submitting the panel is the one case that should jump back to page 1: the
  // page you were on no longer means anything against a different result set.
  const handleFilter = () => {
    const next = {
      status: form.getFieldValue('status'),
      listingType: form.getFieldValue('listingType'),
      // The search box lives in the toolbar now, not in this panel, so the
      // panel must carry the applied term through rather than clearing it.
      search: appliedFilters.search,
    };
    setAppliedFilters(next);
    setCurrentPage(1);
    runQuery({filters: next, page: 1});
    setOpenFilterModal(false);
  };

  // Header summary. The old page said nothing about what was on screen; the
  // count and type now carry that so the type control can disappear when the
  // asset class holds only one.
  const total = data?.getFinancialInstruments?.pagination?.total ?? 0;
  const activeLabel = (
    instrumentKeyOptions.find(option => option.key === instrumentKey)?.label ??
    'instruments'
  ).toLowerCase();
  // Counted from what is applied, not from what is typed. Reading
  // `form.getFieldValue` here meant the badge lit up as soon as a status was
  // picked — before Filter was pressed — and, because that read does not
  // subscribe to the form, it then failed to clear until something else
  // re-rendered the page.
  const activeFilterCount = [
    appliedFilters.status,
    appliedFilters.listingType,
    appliedFilters.search,
  ].filter(Boolean).length;

  const handlePageChange = page => {
    if (page === currentPage) return;
    setCurrentPage(page);
    runQuery({page});
  };

  return (
    <div>
      {/* One header card: identity, page actions, then every control on a
          single row. Replaces three stacked 64px toggler bars plus a separate
          filter row — ~280px of chrome before the first row of data. */}
      <div className={shell.head}>
        <div className={shell.headTop}>
          <div>
            <button
              type="button"
              className={shell.back}
              onClick={() => navigate('/dashboard/asset-management/classes')}>
              ← Asset classes
            </button>
            <h1 className={shell.title}>{assetClassLabel(key)}</h1>
            <div className={shell.sub}>
              {activeSubTab === 'GROUP'
                ? `${total.toLocaleString()} group${
                    total === 1 ? '' : 's'
                  } of ${activeLabel}`
                : `${total ? `${total.toLocaleString()} ` : ''}${activeLabel}`}
            </div>
          </div>
          <div className={shell.headActions}>
            {supportsInventoryPrediction(key) && (
              <button
                type="button"
                className={shell.headLink}
                onClick={() =>
                  navigate(
                    `/dashboard/asset-management/class/${key}/inventory-prediction`,
                  )
                }>
                Inventory prediction
              </button>
            )}
            <Dropdown
              trigger="click"
              menu={{
                // Offer exactly the types this asset class holds. Previously
                // hard-coded to Bond and Mutual Fund, so EQUITIES showed two
                // disabled options and no way to create an equity or an IPO.
                items: instrumentKeyOptions.map(option => ({
                  key: option.key,
                  label: option.create,
                  onClick: () =>
                    navigate(
                      `/dashboard/asset-management/create/${option.route}/${key}`,
                    ),
                })),
              }}>
              <div>
                <Button
                  disabled={!pageOperations?.create}
                  label="Create Instrument"
                />
              </div>
            </Dropdown>
          </div>
        </div>

        <div className={shell.controls}>
          {/* A single type is not a choice — the count in the subtitle already
              says what is listed, so the control only appears when it does
              something. */}
          {instrumentKeyOptions.length > 1 && (
            <div className={shell.segmented}>
              {instrumentKeyOptions.map(option => (
                <button
                  type="button"
                  key={option.key}
                  onClick={() => setInstrumentKey(option.key)}
                  className={`${shell.segment} ${
                    instrumentKey === option.key ? shell.segmentActive : ''
                  }`}>
                  {option.label}
                </button>
              ))}
            </div>
          )}

          {/* Between the two segmented controls rather than inside the
              filter panel: searching is the thing operators reach for
              constantly, and it was two clicks deep behind Filter. */}
          <div className={shell.controlsCenter}>
            <Input
              allowClear
              className={shell.searchInput}
              prefix={<SearchOutlined />}
              placeholder="Search instruments"
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
            />
          </div>

          <div className={shell.controlsRight}>
            <div className={shell.segmented}>
              <button
                type="button"
                onClick={() => {
                  setActiveSubTab('SINGLE');
                  navigate(`?subRoute=SINGLE`);
                }}
                className={`${shell.segment} ${
                  activeSubTab === 'SINGLE' ? shell.segmentActive : ''
                }`}>
                Single
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveSubTab('GROUP');
                  navigate(`?subRoute=GROUP`);
                }}
                className={`${shell.segment} ${
                  activeSubTab === 'GROUP' ? shell.segmentActive : ''
                }`}>
                Grouped
              </button>
            </div>

            {activeSubTab === 'SINGLE' ? (
              <div className={shell.filterAnchor}>
                <button
                  type="button"
                  onClick={() => setOpenFilterModal(!openFilterModal)}
                  className={`${shell.filterButton} ${
                    activeFilterCount ? shell.filterButtonOn : ''
                  }`}>
                  <img src={filterIcon} alt="" />
                  Filter
                  {activeFilterCount > 0 && (
                    <span className={shell.filterCount}>
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <div
                  style={{display: openFilterModal ? 'block' : 'none'}}
                  className={styles.filterItems}>
                  <h2>Filter Instruments</h2>
                  <Form form={form} layout="vertical" onFinish={handleFilter}>
                    <Form.Item name="status" label="Status">
                      <Select allowClear>
                        {assetClassStatus.map(status => (
                          <Select.Option key={status.value}>
                            {status.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="listingType" label="Listing Type">
                      <Select allowClear>
                        <Select.Option key="LISTED">Listed</Select.Option>
                        <Select.Option key="UNLISTED">Unlisted</Select.Option>
                      </Select>
                    </Form.Item>
                    <SubmitButton label="Filter" />
                  </Form>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className={shell.rowAction}
                disabled={!pageOperations?.create}
                onClick={() =>
                  navigate(
                    `/dashboard/asset-management/${key}/${instrumentKey}/create-instrument-group`,
                  )
                }>
                Create group
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={shell.surface}>
        <Table
          loading={loading}
          dataSource={data?.getFinancialInstruments?.data}
          pagination={false}
          className="antd-table"
          onRow={record => ({
            onClick: () =>
              activeSubTab === 'GROUP'
                ? navigate(
                    `/dashboard/asset-management/${key}/${instrumentKey}/update-instrument-group/${record?.groupId}`,
                  )
                : navigate(`${record?.id}/languages`, {
                    // The chosen instrument key disambiguates the update route
                    // (EQUITIES holds both public equity and IPO); back returns
                    // to this asset-class instruments list.
                    state: {
                      instrumentKey,
                      backTo: `/dashboard/asset-management/class/${key}/instruments`,
                    },
                  }),
            className: 'on-row',
          })}
          columns={activeSubTab === 'GROUP' ? groupColumns : columns}
          // Only the instrument list widens; the grouped view keeps its own
          // narrow column set and has no inventory to show.
          scroll={
            activeSubTab === 'GROUP'
              ? undefined
              : {x: inventoryScrollWidth(instrumentKey, 860)}
          }
        />
        <Pagination
          className="ant-table-pagination"
          current={currentPage}
          onChange={e => handlePageChange(e)}
          total={data?.getFinancialInstruments?.pagination?.total}
          onShowSizeChange={(_, size) => {
            setCurrentPage(1);
            setLimit(size);
            runQuery({page: 1, size});
          }}
        />
      </div>
      <ConfirmModal
        openModal={deleteGroup.open}
        setOpenModal={() => setDeleteGroup({id: null, open: false, input: {}})}
        loading={loadingDeleteGrouped}
        title="Delete Grouped Instruments"
        confirmText="Are you sure you want to delete this group? This action cannot be undone."
        handleOperation={() =>
          ungroupFinancialInstruments({
            variables: {
              input: {
                id: deleteGroup.id,
                title: deleteGroup.input.title,
                instrumentKey: deleteGroup.input.instrumentKey,
                assetClassKey: deleteGroup.input.assetClassKey,
              },
            },
          }).then(({data: {ungroupFinancialInstruments}}) => {
            if (ungroupFinancialInstruments.__typename === 'Error') {
              toast.error(ungroupFinancialInstruments.message);
            } else {
              toast.success('Deleted Successfully');
              setDeleteGroup({id: null, open: false, input: {}});
              refetch();
            }
          })
        }
      />
      {actionModals}
    </div>
  );
};
